import React, { useRef, useImperativeHandle, useState } from "react";
import clsx from "clsx";
import { FormControlLabel, Checkbox, Box, Typography } from "@curves/ui-base";

/**
 * 個人資料蒐集告知
 *
 * 與 NoticeCard 共用 .notice-card 骨架、欄寬與圖示欄：勾選框同樣放在標題前面。
 * ref 介面刻意與 NoticeCard 一致（getResult / isError），呼叫端才能與其他須知卡片
 * 共用同一個 ref 陣列。
 *
 * @param {object} notice 告知內容 { title, preface, terms: [{ label, text }], rights }
 * @param {string} img 左欄圖示路徑
 * @param {boolean} defaultValue 預設是否已勾選
 * @param {boolean} readOnly 是否唯讀
 * @param {boolean} required 是否必須勾選
 */
const PrivacyNoticeCard = React.forwardRef(({ notice, img, className, defaultValue = false, readOnly, required }, ref) => {
	const { title, preface, terms = [], rights } = notice || {};
	const checkRef = useRef(null);
	const [errorText, setErrorText] = useState(null);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => checkRef.current,
			isError: () => {
				const isChecked = Boolean(checkRef.current?.checked);
				setErrorText(required && !isChecked ? "請先閱讀內容，並勾選以確認" : null);
				return required ? !isChecked : false;
			},
		}),
		// eslint-disable-next-line
		[required],
	);

	return (
		<Box className={clsx(className, "notice-card", "notice-card--legal")}>
			<Box className="notice-card-img">{img && <img className="img-fluid" src={img} alt={title} />}</Box>
			<Box className="notice-card-content">
				<FormControlLabel
					className="control"
					control={<Checkbox inputRef={el => (checkRef.current = el)} defaultChecked={defaultValue} />}
					label={title}
					readOnly={readOnly}
				/>
				{errorText && <Box className="text-error">{errorText}</Box>}
				<Box className="notice-card-legal-body">
					{preface && <Typography className="notice-card-legal-preface">{preface}</Typography>}
					{terms.length > 0 && (
						<Box component="dl" className="notice-card-terms">
							{terms.map(({ label, text }) => (
								<React.Fragment key={label}>
									<Box component="dt" className="notice-card-terms-label">
										{label}
									</Box>
									<Box component="dd" className="notice-card-terms-text">
										{text}
									</Box>
								</React.Fragment>
							))}
						</Box>
					)}
					{rights && <Typography className="notice-card-legal-rights">{rights}</Typography>}
				</Box>
			</Box>
		</Box>
	);
});

export default PrivacyNoticeCard;
